<template>
  <div class="public-product-detail">
    <div v-if="isLoading" class="loading-container">
      <b-spinner variant="primary"></b-spinner>
      <p class="mt-2">Đang tải thông tin...</p>
    </div>
    
    <div v-else-if="productData" class="product-container">
      <!-- Header with title and tags -->


      <!-- Main content -->
      <div class="row">
        <!-- Left column - Property details -->
        <div class="col-lg-8 col-md-12">
          <!-- Image Gallery Section -->
          <div class="image-gallery-section mb-4" v-if="productImages && productImages.length > 0">
            <div class="main-image-container">
              <img 
                :src="selectedMainImage || productImages[0]" 
                :alt="productData.address"
                class="main-image"
                @click="openImageModal(selectedMainImage || productImages[0])"
              />
              <div class="image-overlay">
                <b-button variant="light" size="sm" @click="openImageModal(selectedMainImage || productImages[0])">
                  <b-icon icon="zoom-in"></b-icon> Xem toàn màn hình
                </b-button>
              </div>
            </div>
            
            <!-- Thumbnail images -->
            <div class="thumbnail-container" v-if="productImages.length > 1">
              <div 
                v-for="(image, index) in productImages" 
                :key="index"
                class="thumbnail-item"
                :class="{ active: selectedMainImage === image }"
                @click="selectMainImage(image)"
              >
                <img :src="image" :alt="`Hình ảnh ${index + 1}`" />
              </div>
            </div>
          </div>
          

          
          <!-- No images placeholder -->
          <div class="no-images-placeholder mb-4" v-if="!productImages || productImages.length === 0">
            <div class="placeholder-container">
              <b-icon icon="image" font-scale="3" variant="secondary"></b-icon>
              <p class="mt-2">Chưa có hình ảnh cho bất động sản này</p>
            </div>
          </div>

          <div class="property-details-card">
            <h3 class="section-title">
              📋 Thông tin chi tiết bất động sản

              
            </h3>

            <div class="property-tags mt-2">
                <b-badge variant="primary" class="mr-2" v-if="productData.type">{{ productData.type }}</b-badge>
                <b-badge variant="success" class="mr-2" v-if="productData.price">{{ formatPrice(productData.price) }}</b-badge>
                <b-badge variant="info" v-if="productData.area">{{ productData.area }} m²</b-badge>
                <b-badge variant="warning" v-if="productData.status">{{ productData.status }}</b-badge>
            </div>
            
            <!-- Property specifications -->
            <div class="property-specs">
              <div class="spec-row" v-if="productData.area">
                <span class="spec-icon">🏠</span>
                <span class="spec-label">Diện tích: {{ productData.area }} m² 
                  <span v-if="productData.areaWidth && productData.areaLength">
                    ({{ productData.areaWidth }}m x {{ productData.areaLength }}m)
                  </span>
                </span>
              </div>
              
              <div class="spec-row" v-if="productData.houseArea">
                <span class="spec-icon">🏡</span>
                <span class="spec-label">Diện tích nhà: {{ productData.houseArea }} m²
                  <span v-if="productData.houseAreaWidth && productData.houseAreaLength">
                    ({{ productData.houseAreaWidth }}m x {{ productData.houseAreaLength }}m)
                  </span>
                </span>
              </div>
              
              <div class="spec-row" v-if="productData.trait">
                <span class="spec-icon">📝</span>
                <span class="spec-label">Đặc điểm: {{ productData.trait }}</span>
              </div>
              
              <div class="spec-row" v-if="productData.structure">
                <span class="spec-icon">🏗️</span>
                <span class="spec-label">Cấu trúc: {{ productData.structure }}</span>
              </div>
              
              <div class="spec-row" v-if="productData.direction">
                <span class="spec-icon">🧭</span>
                <span class="spec-label">Hướng nhà: {{ productData.direction }}</span>
              </div>
              
              <div class="spec-row" v-if="productData.project">
                <span class="spec-icon">🏢</span>
                <span class="spec-label">Dự án: {{ productData.project }}</span>
              </div>
              
              <div class="spec-row" v-if="productData.usageType">
                <span class="spec-icon">🏘️</span>
                <span class="spec-label">Loại đất: {{ productData.usageType }}</span>
              </div>
              
              <div class="spec-row" v-if="productData.line">
                <span class="spec-icon">🛣️</span>
                <span class="spec-label">Lòng đường: {{ productData.line }}m</span>
              </div>
              
              <div class="spec-row" v-if="productData.lineType">
                <span class="spec-icon">🛣️</span>
                <span class="spec-label">Loại đường: {{ productData.lineType }}</span>
              </div>
              
              <div class="spec-row price-row" v-if="productData.price">
                <span class="spec-icon">💰</span>
                <span class="spec-label">Giá bán: <span class="price-value">{{ formatPrice(productData.price) }} tỷ</span></span>
              </div>
              
              <div class="spec-row" v-if="productData.pricePerArea">
                <span class="spec-icon">📊</span>
                <span class="spec-label">Giá theo m²: {{ productData.pricePerArea }} triệu/m²</span>
              </div>
            </div>

            <!-- Property description -->
            <div class="property-description mt-4" v-if="productData.notes">
              <h4>📄 Mô tả chi tiết</h4>
              <div class="description-content">{{ productData.notes }}</div>
            </div>

            <!-- Positive features -->
            <div class="positive-features mt-4" v-if="productData.positive && productData.positive.length > 0">
              <h4>✨ Ưu điểm nổi bật</h4>
              <div class="features-grid">
                <b-badge 
                  v-for="feature in productData.positive" 
                  :key="feature"
                  variant="success" 
                  class="mr-2 mb-2 feature-badge"
                >
                  {{ feature }}
                </b-badge>
              </div>
            </div>

            <!-- Location info -->
            <div class="location-info mt-4">
              <div class="spec-row" v-if="productData.ward">
                <span class="spec-icon">🏘️</span>
                <span class="spec-label">Phường/Xã: {{ productData.ward }}</span>
              </div>
              <div class="spec-row" v-if="productData.district">
                <span class="spec-icon">🏛️</span>
                <span class="spec-label">Quận/Huyện: {{ productData.district }}</span>
              </div>
            </div>

          </div>
        </div>

        <!-- Right column - Contact form -->
        <div class="col-lg-4 col-md-12">
          <div class="contact-card">
            <h4 class="contact-title">📞 Liên hệ tư vấn</h4>
            <p class="contact-subtitle">Để lại thông tin để nhận tư vấn miễn phí</p>
            
            <b-form @submit="onSubmit" v-if="!submitted">
              <b-form-group>
                <label class="form-label">Họ tên <span class="required">*</span></label>
                <b-form-input
                  v-model="contactForm.fullname"
                  placeholder="Nhập họ tên của bạn"
                  required
                ></b-form-input>
              </b-form-group>

              <b-form-group>
                <label class="form-label">Số điện thoại <span class="required">*</span></label>
                <b-form-input
                  v-model="contactForm.phone"
                  placeholder="Nhập số điện thoại"
                  required
                ></b-form-input>
              </b-form-group>

              <b-form-group>
                <label class="form-label">Nhu cầu của bạn</label>
                <b-form-textarea
                  v-model="contactForm.needs_contact"
                  rows="3"
                  placeholder="Mô tả nhu cầu của bạn..."
                ></b-form-textarea>
              </b-form-group>

              <b-button 
                type="submit" 
                variant="success" 
                class="w-100"
                :disabled="isSubmitting"
              >
                {{ isSubmitting ? "Đang gửi..." : "Gửi thông tin liên hệ" }}
              </b-button>
            </b-form>

            <div v-else class="success-message">
              <b-icon icon="check-circle-fill" variant="success" font-scale="2"></b-icon>
              <h5 class="mt-2">Cảm ơn bạn!</h5>
              <p>Chúng tôi sẽ liên hệ lại trong thời gian sớm nhất.</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="error-container">
      <b-icon icon="exclamation-triangle" font-scale="3" variant="warning"></b-icon>
      <h3 class="mt-3">Không tìm thấy thông tin</h3>
      <p>Bất động sản này có thể đã được gỡ bỏ hoặc không tồn tại.</p>
    </div>

    <!-- Image Modal -->
    <b-modal v-model="showImageModal" size="xl" hide-footer centered>
      <template #modal-header>
        <h5 class="modal-title">Hình ảnh bất động sản</h5>
        <button type="button" class="close" @click="showImageModal = false">
          <span>&times;</span>
        </button>
      </template>
      <div class="modal-image-container">
        <img :src="selectedImage" class="modal-image" v-if="selectedImage" />
      </div>
    </b-modal>
  </div>
</template>

<script>
import ProductService from '@/services/productService'
import axios from 'axios'
import Constants from '@/constants'
import moment from 'moment'

export default {
  name: 'PublicProductViewDetail',
  data() {
    return {
      productData: null,
      productImages: [],
      selectedMainImage: null,
      isLoading: true,
      contactForm: {
        fullname: '',
        phone: '',
        needs_contact: ''
      },
      isSubmitting: false,
      submitted: false,
      showImageModal: false,
      selectedImage: null
    }
  },
  async mounted() {
    await this.loadProductData()
  },
  methods: {
    async loadProductData() {
      try {
        this.isLoading = true
        const productId = this.$route.params.id
        
        if (!productId) {
          this.productData = null
          return
        }

        const service = new ProductService()
        const data = await service.getDetail(productId)
        
        if (data) {
          this.productData = data
          this.processImages(data)
        } else {
          this.productData = null
        }
      } catch (error) {
        console.error('Error loading product:', error)
        this.productData = null
      } finally {
        this.isLoading = false
      }
    },

    processImages(data) {
      this.productImages = []
      
      // Helper function to safely filter URLs
      const filterValidUrls = (urls) => {
        return urls.filter(url => {
          return url && typeof url === 'string' && url.trim().length > 0
        })
      }
      
      // Helper function to extract URLs from image objects
      const extractUrlsFromImageObjects = (imageArray) => {
        if (!Array.isArray(imageArray)) return []
        
        return imageArray
          .filter(item => item && typeof item === 'object' && item.url)
          .map(item => item.url)
          .filter(url => url && typeof url === 'string' && url.trim().length > 0)
      }
      
      // Try to get images from image_list field first (JSON format)
      if (data.image_list) {
        try {
          const imageList = typeof data.image_list === 'string' 
            ? JSON.parse(data.image_list) 
            : data.image_list
          
          if (Array.isArray(imageList)) {
            // Check if it's an array of objects with url property
            if (imageList.length > 0 && typeof imageList[0] === 'object' && imageList[0].url) {
              this.productImages = extractUrlsFromImageObjects(imageList)
            } else {
              // Handle as simple array of strings
              this.productImages = filterValidUrls(imageList)
            }
          } else if (typeof imageList === 'object') {
            // Handle object format
            const allUrls = Object.values(imageList).flat()
            this.productImages = filterValidUrls(allUrls)
          }
        } catch (e) {
          console.log('Error parsing image_list:', e)
        }
      }
      
      // Fallback to images field if image_list is empty
      if (this.productImages.length === 0 && data.images) {
        try {
          const imagesData = JSON.parse(data.images)
          if (typeof imagesData === 'object') {
            const allUrls = Object.values(imagesData).flat()
            this.productImages = filterValidUrls(allUrls)
          }
        } catch (e) {
          console.log('Error parsing images:', e)
        }
      }
      

      
      // Set first image as selected main image
      if (this.productImages.length > 0) {
        this.selectedMainImage = this.productImages[0]
      }
    },

    selectMainImage(image) {
      this.selectedMainImage = image
    },

    formatPrice(price) {
      if (!price) return ''
      const numPrice = parseFloat(price)
      if (numPrice >= 1000) {
        return (numPrice / 1000).toFixed(1)
      }
      return numPrice.toLocaleString('vi-VN')
    },

    openMap() {
      if (this.productData.gps) {
        const location = this.productData.gps.replace(/\s/g, "")
        const url = `https://www.google.com/maps/search/?api=1&query=${location}`
        window.open(url, '_blank')
      }
    },

    openImageModal(image) {
      this.selectedImage = image
      this.showImageModal = true
    },

    async onSubmit(event) {
      event.preventDefault()
      
      if (this.isSubmitting) return
      
      this.isSubmitting = true
      
      try {
        // Use the same structure as existing contact forms
        const columns = ['code', 'fullname', 'phone', 'needs_contact', 'created_at', 'input_by', 'sale', 'source', 'status', 'created_by']
        const values = [
          `'KH${moment().format('YYMMDD-HHmmss')}'`,
          `'${this.contactForm.fullname}'`,
          `'${this.contactForm.phone}'`,
          `'${this.contactForm.needs_contact} - Quan tâm SP: ${this.productData?.address || this.$route.params.id}'`,
          `'${moment().format('YYYY-MM-DD HH:mm:ss')}'`,
          `'PUBLIC_USER'`,
          `'TUẤN'`,
          `'optin form của angiangnhadat'`,
          `'Data mới'`,
          `'public_user'`
        ]

        const query = `INSERT INTO realestate.clients (${columns.join(',')}) VALUES (${values.join(',')})`
        
        const url = Constants.HOST_URL + 'runQuery/insert'
        await axios.post(url, { query })

        this.submitted = true
        
        // Reset form
        this.contactForm = {
          fullname: '',
          phone: '',
          needs_contact: ''
        }
      } catch (error) {
        console.error('Error submitting contact form:', error)
        alert('Có lỗi xảy ra khi gửi thông tin. Vui lòng thử lại.')
      } finally {
        this.isSubmitting = false
      }
    }
  }
}
</script>

<style scoped>
.public-product-detail {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 20px 0;
}

.loading-container, .error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  text-align: center;
}

.product-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 15px;
}

.product-header {
  text-align: center;
  background: white;
  padding: 30px;
  border-radius: 15px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  margin-bottom: 30px;
}

.product-title {
  font-size: 2rem;
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 15px;
  line-height: 1.3;
}

.property-tags .badge {
  font-size: 0.9rem;
  padding: 8px 12px;
}

/* Image Gallery Styles */
.image-gallery-section {
  background: white;
  border-radius: 15px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  overflow: hidden;
}

.main-image-container {
  position: relative;
  height: 400px;
  overflow: hidden;
  cursor: pointer;
}

.main-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.main-image:hover {
  transform: scale(1.05);
}

.image-overlay {
  position: absolute;
  top: 10px;
  right: 10px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.main-image-container:hover .image-overlay {
  opacity: 1;
}

.thumbnail-container {
  display: flex;
  gap: 10px;
  padding: 15px;
  overflow-x: auto;
}

.thumbnail-item {
  flex-shrink: 0;
  width: 80px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.3s ease;
}

.thumbnail-item:hover {
  border-color: #007bff;
  transform: scale(1.05);
}

.thumbnail-item.active {
  border-color: #007bff;
  box-shadow: 0 0 10px rgba(0,123,255,0.3);
}

.thumbnail-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}



.property-details-card {
  background: white;
  padding: 30px;
  border-radius: 15px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  margin-bottom: 20px;
}

.section-title {
  font-size: 1.5rem;
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 25px;
  text-align: center;
}

.property-specs {
  margin-bottom: 25px;
}

.spec-row {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #ecf0f1;
}

.spec-row:last-child {
  border-bottom: none;
}

.spec-icon {
  font-size: 1.2rem;
  margin-right: 10px;
  width: 25px;
}

.spec-label {
  font-weight: 600;
  color: #34495e;
  flex: 1;
}

.price-row {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  border: none !important;
  margin: 15px 0;
}

.price-value {
  font-size: 1.2rem;
  font-weight: bold;
  color: #27ae60;
}

.description-content {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  line-height: 1.6;
  white-space: pre-line;
}

.positive-features {
  margin-bottom: 25px;
}

.features-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.feature-badge {
  font-size: 0.85rem;
  padding: 6px 10px;
}

.contact-card {
  background: white;
  padding: 25px;
  border-radius: 15px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  position: sticky;
  top: 20px;
}

.contact-title {
  color: #2c3e50;
  font-weight: bold;
  margin-bottom: 10px;
}

.contact-subtitle {
  color: #7f8c8d;
  margin-bottom: 20px;
  font-size: 0.9rem;
}

.form-label {
  font-weight: 600;
  color: #34495e;
  margin-bottom: 8px;
}

.required {
  color: #e74c3c;
}

.success-message {
  text-align: center;
  padding: 20px;
  color: #27ae60;
}

/* Modal Styles */
.modal-image-container {
  text-align: center;
}

.modal-image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
}

/* Placeholder styles */
.no-images-placeholder {
  background: white;
  border-radius: 15px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  padding: 40px;
  text-align: center;
}

.placeholder-container {
  color: #6c757d;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .product-title {
    font-size: 1.5rem;
  }
  
  .property-details-card,
  .contact-card {
    padding: 20px;
  }
  
  .spec-row {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .spec-label {
    min-width: auto;
    margin-bottom: 5px;
  }
  
  .contact-card {
    position: static;
    margin-top: 20px;
  }
  
  .main-image-container {
    height: 250px;
  }
  
  .thumbnail-item {
    width: 60px;
    height: 45px;
  }
  
  .features-grid {
    justify-content: center;
  }
}

/* Tablet styles */
@media (min-width: 769px) and (max-width: 992px) {
  .main-image-container {
    height: 300px;
  }
}
</style> 